import {
  DatePickerDate,
  DatePickerInput,
  DatePickerInputValidationResult,
} from '@smartsheet/lodestar-core';
import { addDays } from 'date-fns';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimer } from '../hooks/useTimer';
import { locale, ScheduleStateDatePickerType } from './utils';

export const StartDate = ({
  startDate,
  setStartDate,
  isRequired,
}: {
  startDate: ScheduleStateDatePickerType;
  setStartDate: (value: ScheduleStateDatePickerType) => void;
  isRequired?: boolean;
}) => {
  const [startDateValidState, setStartDateValidState] =
    useState<DatePickerInputValidationResult>('isValid');
  const { value: startDateValue } = startDate;
  const { t: translate } = useTranslation();

  const [liveUTCDateTime] = useTimer();

  let dateToUse = liveUTCDateTime;
  if (
    liveUTCDateTime.getUTCHours() === 23 &&
    liveUTCDateTime.getUTCMinutes() >= 45
  ) {
    dateToUse = addDays(new Date(), 1);
  }
  const minDate = useMemo(() => {
    return {
      day: dateToUse.getUTCDate(),
      month: dateToUse.getUTCMonth(),
      year: dateToUse.getUTCFullYear(),
    };
  }, [dateToUse]);

  const isStartDateValid = useMemo(
    () =>
      isRequired
        ? startDateValidState === 'isValid' && startDateValue !== null
        : startDateValidState === 'isValid',
    [startDateValidState, isRequired, startDateValue]
  );

  const dateChangeHandler = useCallback(
    (date: DatePickerDate | null) => {
      setStartDate({
        value: date,
        valid: isStartDateValid,
      });
    },
    [isStartDateValid, setStartDate]
  );

  useEffect(() => {
    setStartDate({
      value: startDateValue,
      valid: isStartDateValid,
    });
  }, [startDateValidState, startDateValue, setStartDate, isStartDateValid]);

  return (
    <DatePickerInput
      onValidationStateChange={setStartDateValidState}
      isRequired={isRequired}
      format={translate('dateFormat:monthFirst')}
      label={translate('designer:triggerPanel:schedules:upsert:startDate')}
      locale={locale}
      minDate={minDate}
      onChange={dateChangeHandler}
      value={startDateValue}
      width="responsive"
    />
  );
};
